import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, MessageSquare, Layers } from "lucide-react";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

export default function IDGChatboxTab() {
  const [rooms, setRooms] = useState([]);
  const [settings, setSettings] = useState({
    announcement_text: "",
    polls_enabled: false,
    ai_api_key: "",
    allowed_roles_view: ["user", "vip", "mod", "admin", "root"],
    allowed_roles_post: ["user", "vip", "mod", "admin", "root"],
    allowed_boards: ["all"],
    widget_width: "",
    widget_height: "400px",
    widget_padding: "",
    chat_font_family: "Inter",
    chat_inner_padding: "normal",
    allowed_roles_media: ["user", "vip", "mod", "admin", "root"],
    allowed_roles_mention: ["user", "vip", "mod", "admin", "root"]
  });
  const [boards, setBoards] = useState([]);

  const loadRooms = () => api.get("/idg/admin/chat/rooms").then((r) => setRooms(r.data));
  const loadSettings = () => api.get("/idg/admin/chat/settings").then((r) => setSettings((prev) => ({ ...prev, ...r.data })));
  const loadBoards = () => api.get("/boards").then((r) => setBoards([{id: "all", name: "Toate Forumurile"}, ...r.data]));

  useEffect(() => {
    loadRooms();
    loadSettings();
    loadBoards();
  }, []);

  const toggleList = (key, val) => {
    setSettings((s) => {
      const list = s[key] || [];
      const has = list.includes(val);
      if (val === "all" && key === "allowed_boards") return { ...s, [key]: ["all"] };
      const next = has ? list.filter((x) => x !== val) : [...list.filter(x => x !== "all"), val];
      return { ...s, [key]: next.length ? next : (key === "allowed_boards" ? ["all"] : []) };
    });
  };

  const createRoom = async () => {
    const name = window.prompt("Nume Cameră (ex: Lobby):");
    if (!name) return;
    try {
      await api.post("/idg/admin/chat/rooms", { name, description: "", order: rooms.length, enabled: true });
      toast.success("Cameră adăugată");
      loadRooms();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const updateRoom = async (id, payload) => {
    try {
      await api.patch(`/idg/admin/chat/rooms/${id}`, payload);
      toast.success("Cameră actualizată");
      loadRooms();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteRoom = async (id) => {
    if (!window.confirm("Ești sigur că ștergi această cameră și toate mesajele din ea?")) return;
    try {
      await api.delete(`/idg/admin/chat/rooms/${id}`);
      toast.success("Cameră ștearsă");
      loadRooms();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveSettings = async () => {
    try {
      await api.patch("/idg/admin/chat/settings", settings);
      toast.success("Setări Chat salvate");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIdx = rooms.findIndex((x) => x.id === active.id);
      const newIdx = rooms.findIndex((x) => x.id === over.id);
      const next = arrayMove(rooms, oldIdx, newIdx);
      setRooms(next);
      // Salvează order in backend (could optimize by sending an array, but standard loop works for small sets)
      for (let i = 0; i < next.length; i++) {
        if (next[i].order !== i) {
          await api.patch(`/idg/admin/chat/rooms/${next[i].id}`, { order: i });
        }
      }
    }
  };

  return (
    <div className="space-y-6" data-testid="idg-chatbox-tab">
      <div className="flex items-center gap-2">
        <MessageSquare className="w-5 h-5 text-[var(--tf-primary)]" />
        <h3 className="text-lg font-bold tf-heading text-white tracking-widest">Gestiune Chatbox</h3>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Setări Generale */}
        <div className="space-y-4">
          <div className="tf-surface-elev tf-clip-card p-4 space-y-4">
            <h4 className="text-sm font-bold text-white mb-2 uppercase tracking-widest tf-heading border-b border-white/5 pb-2">Setări Generale</h4>
            
            <label className="block space-y-1">
              <span className="text-xs text-zinc-400 font-bold uppercase tracking-widest">Text Anunț (Apare sus pe chatbox)</span>
              <input
                type="text"
                className="w-full bg-black/50 border border-white/10 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--tf-primary)]"
                value={settings.announcement_text || ""}
                onChange={(e) => setSettings({ ...settings, announcement_text: e.target.value })}
                placeholder="Ex: Please do not discuss bans in the chatbox..."
              />
            </label>

            <label className="flex items-center gap-2 cursor-pointer mt-4">
              <input
                type="checkbox"
                checked={settings.polls_enabled || false}
                onChange={(e) => setSettings({ ...settings, polls_enabled: e.target.checked })}
                className="accent-[var(--tf-primary)]"
              />
              <span className="text-xs font-bold uppercase tracking-widest text-zinc-300">Activează Tab "Polls"</span>
            </label>

            <div className="pt-4 border-t border-white/5 space-y-4">
              <h5 className="text-xs font-bold text-white uppercase tracking-widest">Dimensiuni Widget</h5>
              <div className="grid grid-cols-3 gap-2">
                <label className="block space-y-1">
                  <span className="text-[10px] text-zinc-400 font-bold uppercase">Înălțime (ex: 400px)</span>
                  <input
                    type="text"
                    className="w-full bg-black/50 border border-white/10 rounded px-2 py-1 text-sm text-white focus:outline-none focus:border-[var(--tf-primary)]"
                    value={settings.widget_height || ""}
                    onChange={(e) => setSettings({ ...settings, widget_height: e.target.value })}
                    placeholder="400px"
                  />
                </label>
                <label className="block space-y-1">
                  <span className="text-[10px] text-zinc-400 font-bold uppercase">Lățime (ex: 100%)</span>
                  <input
                    type="text"
                    className="w-full bg-black/50 border border-white/10 rounded px-2 py-1 text-sm text-white focus:outline-none focus:border-[var(--tf-primary)]"
                    value={settings.widget_width || ""}
                    onChange={(e) => setSettings({ ...settings, widget_width: e.target.value })}
                    placeholder="Auto / 100%"
                  />
                </label>
                <label className="block space-y-1">
                  <span className="text-[10px] text-zinc-400 font-bold uppercase">Padding</span>
                  <input
                    type="text"
                    className="w-full bg-black/50 border border-white/10 rounded px-2 py-1 text-sm text-white focus:outline-none focus:border-[var(--tf-primary)]"
                    value={settings.widget_padding || ""}
                    onChange={(e) => setSettings({ ...settings, widget_padding: e.target.value })}
                    placeholder="0px"
                  />
                </label>
              </div>
            </div>

            <div className="pt-4 border-t border-white/5 space-y-4">
              <h5 className="text-xs font-bold text-white uppercase tracking-widest">Stil & Aspect</h5>
              <div className="grid grid-cols-2 gap-2">
                <label className="block space-y-1">
                  <span className="text-[10px] text-zinc-400 font-bold uppercase">Font Chatbox</span>
                  <select
                    className="w-full bg-black/50 border border-white/10 rounded px-2 py-1 text-sm text-white focus:outline-none focus:border-[var(--tf-primary)] text-zinc-300"
                    value={settings.chat_font_family || "Inter"}
                    onChange={(e) => setSettings({ ...settings, chat_font_family: e.target.value })}
                  >
                    <option value="Inter">Inter (Default)</option>
                    <option value="Roboto">Roboto</option>
                    <option value="Open Sans">Open Sans</option>
                    <option value="Segoe UI">Segoe UI</option>
                    <option value="Georgia">Georgia</option>
                    <option value="monospace">Monospace</option>
                  </select>
                </label>
                <label className="block space-y-1">
                  <span className="text-[10px] text-zinc-400 font-bold uppercase">Spațiere Mesaje</span>
                  <select
                    className="w-full bg-black/50 border border-white/10 rounded px-2 py-1 text-sm text-white focus:outline-none focus:border-[var(--tf-primary)] text-zinc-300"
                    value={settings.chat_inner_padding || "normal"}
                    onChange={(e) => setSettings({ ...settings, chat_inner_padding: e.target.value })}
                  >
                    <option value="compact">Compact (Dense)</option>
                    <option value="normal">Normal (Standard)</option>
                    <option value="cozy">Cozy (Larger)</option>
                  </select>
                </label>
              </div>
            </div>

            <div className="pt-4 border-t border-white/5 space-y-4">
              <label className="block space-y-1">
                <span className="text-xs text-[var(--tf-primary)] font-bold uppercase tracking-widest">Cheie API Asistent AI (OpenAI/Gemini)</span>
                <input
                  type="password"
                  className="w-full bg-black/50 border border-white/10 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--tf-primary)]"
                  value={settings.ai_api_key || ""}
                  onChange={(e) => setSettings({ ...settings, ai_api_key: e.target.value })}
                  placeholder="ex: sk-... sau al-..."
                />
              </label>

              <div>
                <span className="text-xs text-zinc-400 font-bold uppercase tracking-widest block mb-2">Permisiuni Vizualizare</span>
                <div className="flex gap-2 flex-wrap">
                  {["user", "vip", "mod", "admin"].map(r => (
                    <button key={r} onClick={() => toggleList("allowed_roles_view", r)} className={`px-2 py-1 text-[10px] uppercase font-bold rounded border ${settings.allowed_roles_view?.includes(r) ? 'bg-[var(--tf-primary)] text-black border-[var(--tf-primary)]' : 'bg-transparent text-zinc-400 border-white/10 hover:border-white/30'}`}>{r}</button>
                  ))}
                </div>
              </div>

              <div>
                <span className="text-xs text-zinc-400 font-bold uppercase tracking-widest block mb-2">Permisiuni Scriere</span>
                <div className="flex gap-2 flex-wrap">
                  {["user", "vip", "mod", "admin"].map(r => (
                    <button key={r} onClick={() => toggleList("allowed_roles_post", r)} className={`px-2 py-1 text-[10px] uppercase font-bold rounded border ${settings.allowed_roles_post?.includes(r) ? 'bg-[var(--tf-primary)] text-black border-[var(--tf-primary)]' : 'bg-transparent text-zinc-400 border-white/10 hover:border-white/30'}`}>{r}</button>
                  ))}
                </div>
              </div>

              <div>
                <span className="text-xs text-zinc-400 font-bold uppercase tracking-widest block mb-2">Permisiuni Postare Media (/yt, /tk, /ig, imagini)</span>
                <div className="flex gap-2 flex-wrap">
                  {["user", "vip", "mod", "admin"].map(r => (
                    <button key={r} onClick={() => toggleList("allowed_roles_media", r)} className={`px-2 py-1 text-[10px] uppercase font-bold rounded border ${settings.allowed_roles_media?.includes(r) ? 'bg-[var(--tf-primary)] text-black border-[var(--tf-primary)]' : 'bg-transparent text-zinc-400 border-white/10 hover:border-white/30'}`}>{r}</button>
                  ))}
                </div>
              </div>

              <div>
                <span className="text-xs text-zinc-400 font-bold uppercase tracking-widest block mb-2">Permisiuni Mențiune Roluri (@administrator)</span>
                <div className="flex gap-2 flex-wrap">
                  {["user", "vip", "mod", "admin"].map(r => (
                    <button key={r} onClick={() => toggleList("allowed_roles_mention", r)} className={`px-2 py-1 text-[10px] uppercase font-bold rounded border ${settings.allowed_roles_mention?.includes(r) ? 'bg-[var(--tf-primary)] text-black border-[var(--tf-primary)]' : 'bg-transparent text-zinc-400 border-white/10 hover:border-white/30'}`}>{r}</button>
                  ))}
                </div>
              </div>

              <div>
                <span className="text-xs text-zinc-400 font-bold uppercase tracking-widest block mb-2">Afișare pe Forumuri</span>
                <div className="flex gap-2 flex-wrap">
                  {boards.map(b => (
                    <button key={b.id} onClick={() => toggleList("allowed_boards", b.id)} className={`px-2 py-1 text-[10px] uppercase font-bold rounded border ${settings.allowed_boards?.includes(b.id) ? 'bg-blue-500 text-white border-blue-500' : 'bg-transparent text-zinc-400 border-white/10 hover:border-white/30'}`}>{b.name.substring(0,20)}</button>
                  ))}
                </div>
              </div>
            </div>

            <button onClick={saveSettings} className="tf-btn-primary tf-clip-btn text-xs flex items-center gap-2 w-full justify-center">
              <Save className="w-4 h-4" /> Salvează Setări
            </button>
          </div>
        </div>

        {/* Camere */}
        <div className="space-y-4">
          <div className="tf-surface-elev tf-clip-card p-4 space-y-4">
            <div className="flex items-center justify-between border-b border-white/5 pb-2">
              <h4 className="text-sm font-bold text-white uppercase tracking-widest tf-heading">Camere (Rooms)</h4>
              <button onClick={createRoom} className="text-xs tf-btn-secondary tf-clip-btn flex items-center gap-1">
                <Plus className="w-3 h-3" /> Adaugă Cameră
              </button>
            </div>

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={rooms.map((r) => r.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-2">
                  {rooms.map((r) => (
                    <SortableRoomItem key={r.id} room={r} onUpdate={updateRoom} onDelete={deleteRoom} />
                  ))}
                  {rooms.length === 0 && <p className="text-xs text-zinc-500 italic">Nu există camere. Adaugă "Lobby" pentru a începe.</p>}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        </div>
      </div>
    </div>
  );
}

function SortableRoomItem({ room, onUpdate, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: room.id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <div ref={setNodeRef} style={style} className="flex flex-col gap-2 p-3 bg-black/40 border border-white/5 tf-clip-card-sm hover:border-white/10 transition-colors group">
      <div className="flex items-center gap-3">
        <div {...attributes} {...listeners} className="cursor-grab text-zinc-500 hover:text-white">
          <Layers className="w-4 h-4" />
        </div>
        <div className="flex-1 space-y-1">
          <input
            type="text"
            className="w-full bg-transparent text-sm font-bold text-white outline-none placeholder-zinc-600 focus:border-b focus:border-white/20"
            value={room.name || ""}
            onChange={(e) => onUpdate(room.id, { name: e.target.value })}
            placeholder="Nume Cameră"
          />
        </div>
        <button onClick={() => onDelete(room.id)} className="text-red-500 hover:text-red-400 p-1 opacity-50 group-hover:opacity-100 transition-opacity">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      <div className="flex items-center gap-4 pl-7 mt-1">
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={room.enabled} onChange={(e) => onUpdate(room.id, { enabled: e.target.checked })} className="accent-[var(--tf-primary)]" />
          <span className="text-[10px] text-zinc-400 font-bold uppercase tracking-widest">Activă</span>
        </label>
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={room.is_staff || false} onChange={(e) => onUpdate(room.id, { is_staff: e.target.checked })} className="accent-[var(--tf-primary)]" />
          <span className="text-[10px] text-zinc-400 font-bold uppercase tracking-widest">Staff Chat (Privată)</span>
        </label>
      </div>
    </div>
  );
}
